<script>
import debounce from 'lodash.debounce'
import Dropdown from '@/components/atoms/Dropdown'
import NavPill from '@/components/atoms/NavPill'
import { EventBus } from '@/modules/plugins/modules/event'
import { mapGetters } from 'vuex'

export default {
    name: 'CategoryNavigation',
    components: {
        NavPill,
        Dropdown,
    },
    props: {
        categories: null,
        activeId: null,
    },
    data() {
        return {
            categoriesMore: [],
            widthSum: 0,
            moreVisible: true,
            arrangeTabs: debounce(() => {
                this.arrange()
            }, 100),
            observer: null,
            categoriesWatcherOff: null,
            activeIdWatcherOff: null,
        }
    },
    computed: {
        ...mapGetters({ navScroll: 'site/navScroll', smDevice: 'smDevice' }),
        active() {
            return this.categoriesMore.find(
                (category) => category.id === parseInt(this.$route.params.menu)
            )
        },
        buttonLabel() {
            return this.active
                ? this.active.name
                : this.smDevice
                ? this.$t('labels.categories')
                : this.$t('base.more')
        },
        firstCategory() {
            return this.categories ? this.categories[0] : {}
        },
        deviceSizing() {
            return {
                smDevice: this.smDevice,
                lgDevice: !this.smDevice,
            }
        },
    },
    mounted() {
        this.deviceTypeChange(this.deviceSizing)
        EventBus.$on('deviceTypeChange', this.deviceTypeChange)
        this.setObserver()
        this.$nextTick(() => this._scrollToInitialCategory())
    },
    beforeDestroy() {
        if (EventBus._events?.deviceSizeChange)
            EventBus.$off('deviceSizeChange', this.deviceSizeChange)
        EventBus.$off('deviceTypeChange', this.deviceTypeChange)
        this._categoryScrollDone = true
        clearTimeout(this._categoryScrollTimer)
        try {
            this.disconnect()
        } catch (e) {
            this.$debug.warn(e)
        }
    },
    methods: {
        deviceTypeChange(deviceSizing) {
            if (deviceSizing.lgDevice) {
                this.arrange()
                this.categoriesWatcherOff = this.$watch('categories', (val) => {
                    if (val)
                        this.$nextTick(() => {
                            this.arrange(this.deviceSizing)
                        })
                })
                if (this.activeIdWatcherOff) this.activeIdWatcherOff()
                if (!EventBus._events?.deviceSizeChange)
                    EventBus.$on('deviceSizeChange', this.arrange)
            } else {
                this.categoriesMore = []
                this.widthSum = 0
                this.moreVisible = true
                this.activeIdWatcherOff = this.$watch('activeId', (val) => {
                    this.scrollNavigation(val)
                })
                if (this.categoriesWatcherOff) this.categoriesWatcherOff()
                if (EventBus._events?.deviceSizeChange)
                    EventBus.$off('deviceSizeChange', this.arrange)
            }
        },
        scrollNavigation(id) {
            const element = document.querySelector(`#navCategory-${id}`)
            const container = document.querySelector(`#secondaryNav`)
            if (element && container) {
                container.scrollTo({
                    left: element.getBoundingClientRect().left + container.scrollLeft - 8,
                    behavior: 'smooth',
                })
            }
        },
        pillIsVisible(id) {
            return this.categoriesMore.findIndex((category) => category.id === id) === -1
        },
        arrange() {
            if (this.$refs.navCategory && this.$refs?.more) {
                let more = this.$refs?.more?.$el
                let visibleNavsLength = 0
                this.moreVisible = true
                this.widthSum = more.getBoundingClientRect().width * 2
                this.$set(this, 'categoriesMore', [])
                this.$refs.navCategory.forEach((node) => {
                    if (node && node.$el && node.$el.getBoundingClientRect) {
                        this.widthSum += node.$el.getBoundingClientRect().width + 8
                        if (
                            this.widthSum > this.$el.getBoundingClientRect().width + 8 ||
                            this.smDevice
                        ) {
                            this.categoriesMore.push({
                                id: node.identifier,
                                name: node.$el.innerText,
                            })
                        } else {
                            visibleNavsLength +=
                                node.$el.getBoundingClientRect().width +
                                parseInt(getComputedStyle(node.$el).marginRight.replace('px', ''))
                        }
                    }
                })
                if (this.categoriesMore.length === 0) {
                    this.moreVisible = false
                } else {
                    more.style.maxWidth = `${
                        this.$refs.categories.getBoundingClientRect().width - visibleNavsLength
                    }px`
                }
            }
        },
        _scrollToInitialCategory() {
            const menuId = this.$route.params.menu
            if (!menuId) return
            const el = document.getElementById(`menuCategory-${menuId}`)
            if (!el) return
            this.$scrollTo(el)
            if (this.smDevice) this.scrollNavigation(menuId)
        },
        setObserver() {
            if (window.IntersectionObserver) {
                let options = {
                    rootMargin: '-50% 0% -40% 0%',
                    threshold: 0,
                }

                let callback = (entries) => {
                    if (!this.navScroll) {
                        entries.map((entry) => {
                            if (entry.isIntersecting) {
                                const sectionId = entry.target.dataset.anchor
                                if (
                                    sectionId != this.$route.params.menu &&
                                    !(
                                        this.firstCategory.id == sectionId &&
                                        this.$route.params.menu === undefined
                                    )
                                ) {
                                    this.$router.replace({
                                        params: {
                                            menu:
                                                this.firstCategory.id === sectionId
                                                    ? undefined
                                                    : sectionId,
                                        },
                                        query: this.$route.query,
                                    })
                                }
                            }
                        })
                    }
                }

                this.observer = new IntersectionObserver(callback, options)
                const sections = document.querySelectorAll('.menu-category')
                sections.forEach((section) => {
                    this.observer.observe(section)
                })
            }
        },
        disconnect() {
            if (this.observer) {
                this.observer.disconnect()
                this.observer = null
            }
        },
    },
}
</script>

<template lang="pug">
nav#secondaryNav.nav.nav-pills.nav-secondary(v-if="categories && categories.length" ref="categories")
    nav-pill(
        v-for="(category, index) in categories"
        v-if="category.is_active"
        :key="`navCategory-${index}`"
        :id="`navCategory-${category.id}`"
        :first="!index"
        :identifier="category.id"
        :active="category.id === activeId"
        :sm="smDevice"
        :visible="pillIsVisible(category.id)"
        ref="navCategory") {{category.name}}
    dropdown(
        v-if="!smDevice"
        ref="more"
        :button-classes="['btn btn-dark btn-pill w-100', {'active': active}]"
        button-icon="fa-solid fa-caret-down"
        :class="{'hide-link': !moreVisible}") {{buttonLabel}}
        template(v-slot:content)
            router-link.btn.btn-list-item(
                v-for="(dropdownCategory, index) in categoriesMore"
                :key="`navChild-${index}`"
                :to="{params: {menu: dropdownCategory.id}, query: $route.query}"
                v-scroll-to="`#menuCategory-${dropdownCategory.id}`"
                active-class="active"
                replace exact) {{dropdownCategory.name}}
</template>
